<script setup lang="ts">
definePageMeta({
  layout: 'blank',
})
const { t } = useI18n({
  useScope: 'global',
})
useSeoMeta({ title: t('meta.title') })
const { isAuthenticated } = useSanctumAuth()

const features = ref([
  {
    title: 'Evidencije',
    description: 'Sve potrebne informacije na jednom mestu.',
    icon: 'i-lucide-book-open',
    to: '/',
  },
  {
    title: 'Prevencije',
    description: 'Alarmi, notifikacije i upozorenja za brzu reakciju.',
    icon: 'i-lucide-heart-pulse',
    to: '/',
  },
  {
    title: 'Lokacije za rad / Gradilišta',
    description: 'Praćenje izdvojenih lokacija, gradilišta i pogona.',
    icon: 'i-lucide-map-pin',
    to: '/',
  },
])
const links = ref([
  {
    label: t('platform.explore_features'),
    to: '/',
    color: 'neutral' as const,
    variant: 'subtle' as const,
    trailingIcon: 'i-lucide-arrow-right',
  },
  {
    label: isAuthenticated.value ? t('navigation.home') : t('auth.login'),
    to: isAuthenticated.value ? '/dashboard' : '/auth/login',
    color: 'primary' as const,
    variant: 'subtle' as const,
  },
])

const floatingIcons = [
  { icon: 'i-lucide-hard-hat', top: '8%', left: '10%', size: 'size-10 sm:size-14', delay: '0s', opacity: 'opacity-[0.07]' },
  { icon: 'i-lucide-shield-check', top: '15%', right: '8%', size: 'size-12 sm:size-16', delay: '2s', opacity: 'opacity-[0.06]' },
  { icon: 'i-lucide-cross', top: '55%', right: '15%', size: 'size-8 sm:size-12', delay: '4s', opacity: 'opacity-[0.05]' },
  { icon: 'i-lucide-construction', top: '70%', left: '5%', size: 'size-10 sm:size-14', delay: '1s', opacity: 'opacity-[0.07]' },
  { icon: 'i-lucide-flame', top: '35%', left: '3%', size: 'size-8 sm:size-10', delay: '3s', opacity: 'opacity-[0.05]' },
  { icon: 'i-lucide-triangle-alert', top: '85%', right: '5%', size: 'size-10 sm:size-12', delay: '5s', opacity: 'opacity-[0.06]' },
  { icon: 'i-lucide-eye', top: '45%', right: '3%', size: 'size-8 sm:size-10', delay: '2.5s', opacity: 'opacity-[0.05]' },
  { icon: 'i-lucide-clipboard-check', top: '90%', left: '15%', size: 'size-8 sm:size-12', delay: '3.5s', opacity: 'opacity-[0.06]' },
]
</script>

<template>
  <div class="relative min-h-dvh overflow-hidden">
    <!-- Gradient background -->
    <div class="absolute inset-0 bg-linear-to-br from-orange-50 via-white to-amber-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-orange-950/30" />

    <!-- Decorative blobs -->
    <div class="absolute top-[-10%] right-[-5%] h-125 w-125 rounded-full bg-orange-200/30 blur-3xl dark:bg-orange-900/20" />
    <div class="absolute bottom-[-10%] left-[-5%] h-100 w-100 rounded-full bg-amber-200/20 blur-3xl dark:bg-amber-900/15" />

    <!-- Floating safety icons -->
    <div
      v-for="(item, i) in floatingIcons"
      :key="i"
      class="absolute text-orange-900 dark:text-orange-100 animate-float"
      :style="{
        top: item.top,
        left: item.left,
        right: item.right,
        animationDelay: item.delay,
      }"
    >
      <UIcon :name="item.icon" :class="[item.size, item.opacity]" />
    </div>

    <!-- Content -->
    <UPageSection
      :title="t('platform.welcome')"
      :description="t('meta.description')"
      icon="i-mdi-account-hard-hat-outline"
      orientation="vertical"
      :features="features"
      :links="links"
      :ui="{
        root: 'relative',
        container: 'flex flex-col py-12 sm:py-16 lg:py-24 gap-6 sm:gap-10',
      }"
    />
  </div>
</template>

<style scoped>
@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-12px) rotate(3deg);
  }
  50% {
    transform: translateY(-6px) rotate(-2deg);
  }
  75% {
    transform: translateY(-15px) rotate(1deg);
  }
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}
</style>
